import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Mail, Trash2, UserPlus, Users, Link2, Check } from 'lucide-react'
import { useTrip } from '../trips/useTrip'
import { useAuth } from '../auth/useAuth'
import {
  useInviteParticipant,
  useParticipants,
  useRemoveParticipant,
  useUpdateParticipant,
} from './useParticipants'
import { participantDisplayName } from '../../shared/lib/participant'
import { USE_MOCK_API } from '../../shared/api/httpClient'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import { QrInvitePanel } from './QrInvitePanel'
import type { Participant, ParticipantRole } from '../../shared/api/types'
import { cn } from '../../shared/lib/cn'

const ROLES: ParticipantRole[] = ['owner', 'editor', 'viewer']

function displayName(p: Participant): string {
  const raw = participantDisplayName(p)
  if (!USE_MOCK_API && raw.length > 20) return `${raw.slice(0, 8)}…`
  return raw
}

export function People() {
  const { id } = useParams<{ id: string }>()
  const tripId = id as string
  const { user } = useAuth()
  const trip = useTrip(id)
  const participants = useParticipants(id)
  const invite = useInviteParticipant(tripId)
  const updateRole = useUpdateParticipant(tripId)
  const remove = useRemoveParticipant(tripId)

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<ParticipantRole>('viewer')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const isOwner = trip.data?.owner_id === user?.id

  async function copyInviteLink() {
    const link = `${window.location.origin}/invite/${tripId}`
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Could not copy the invite link.')
    }
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await invite.mutateAsync({ email: email.trim(), role })
      setEmail('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed')
    }
  }

  if (participants.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    )
  }

  const list = participants.data ?? []

  return (
    <div className="mx-auto max-w-2xl pb-12">
      <Link
        to={`/trips/${tripId}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back to overview
      </Link>

      <div className="mb-5 flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-st-md bg-st-primary/10 text-st-primary">
          <Users className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold text-st-text">People</h1>
          <p className="text-sm text-st-muted">{trip.data?.title}</p>
        </div>
      </div>

      {isOwner && (
        <form onSubmit={(e) => void onInvite(e)} className="mb-6 rounded-st-lg border border-st-line bg-st-surface p-4">
          <h2 className="mb-3 text-sm font-semibold text-st-text">Invite someone</h2>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-st-muted" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="friend@example.com"
                className="w-full rounded-st-md border border-st-line bg-st-surface py-2.5 pl-9 pr-3 text-sm text-st-text focus:border-st-primary focus:outline-none"
              />
            </div>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as ParticipantRole)}
              className="rounded-st-md border border-st-line bg-st-surface px-3 py-2.5 text-sm text-st-text"
            >
              <option value="viewer">Viewer</option>
              <option value="contributor">Contributor</option>
              <option value="editor">Editor</option>
            </select>
            <Button type="submit" disabled={invite.isPending}>
              <UserPlus className="h-4 w-4" /> {invite.isPending ? 'Sending…' : 'Invite'}
            </Button>
          </div>
          {error && <p className="mt-2 text-sm text-st-danger">{error}</p>}
          <div className="mt-3 flex items-center justify-between gap-2 border-t border-st-line pt-3">
            <p className="text-xs text-st-muted">
              In local dev, invites are logged on the server — no real email is sent.
            </p>
            <button
              type="button"
              onClick={() => void copyInviteLink()}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-st-md border border-st-line px-3 py-1.5 text-xs font-medium text-st-text hover:bg-st-surface-2"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-st-primary" /> : <Link2 className="h-3.5 w-3.5" />}
              {copied ? 'Copied!' : 'Copy invite link'}
            </button>
          </div>
        </form>
      )}

      {isOwner && <QrInvitePanel tripId={tripId} />}

      {list.length === 0 ? (
        <EmptyState
          Icon={Users}
          title="No participants yet"
          description="Invite travel companions to plan and share this trip together."
        />
      ) : (
        <ul className="space-y-2">
          {list.map((p) => (
            <ParticipantRow
              key={p.user_id}
              participant={p}
              canManage={isOwner && p.role !== 'owner'}
              onRoleChange={(r) => updateRole.mutate({ userId: p.user_id, role: r })}
              onRemove={() => remove.mutate(p.user_id)}
              busy={updateRole.isPending || remove.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function ParticipantRow({
  participant: p,
  canManage,
  onRoleChange,
  onRemove,
  busy,
}: {
  participant: Participant
  canManage: boolean
  onRoleChange: (role: ParticipantRole) => void
  onRemove: () => void
  busy: boolean
}) {
  return (
    <li className="flex items-center gap-3 rounded-st-md border border-st-line bg-st-surface p-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-st-full bg-st-surface-2 text-sm font-medium text-st-primary">
        {displayName(p).charAt(0).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-st-text">{displayName(p)}</p>
        <p className="text-xs text-st-muted capitalize">
          {p.role}
          {p.status === 'invited' ? ' · invited' : ''}
        </p>
      </div>
      {canManage ? (
        <div className="flex items-center gap-1">
          <select
            value={p.role}
            disabled={busy}
            onChange={(e) => onRoleChange(e.target.value as ParticipantRole)}
            className="rounded-st-sm border border-st-line bg-st-surface px-2 py-1 text-xs capitalize text-st-text"
          >
            {ROLES.filter((r) => r !== 'owner').map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={busy}
            onClick={onRemove}
            className="rounded-st-sm p-1.5 text-st-muted hover:bg-st-danger/10 hover:text-st-danger"
            aria-label="Remove participant"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <span
          className={cn(
            'rounded-st-full px-2.5 py-0.5 text-xs font-medium capitalize',
            p.role === 'owner' ? 'bg-st-primary/15 text-st-primary' : 'bg-st-surface-2 text-st-muted',
          )}
        >
          {p.role}
        </span>
      )}
    </li>
  )
}
